import React, { useState, useEffect } from 'react';
import { X, Search, Check, Users, Loader2 } from 'lucide-react';
import { ref, get, push, set, serverTimestamp, update } from 'firebase/database';
import { db } from '../../../../integrations/firebase';
import { GroupService } from '../../services/GroupService';
import { useToast } from '../../../../shared/ui/Toast';

const CreateGroupModal = ({ currentUser, isOpen, onClose, onGroupCreated }) => {
    const { showToast } = useToast();
    const [groupName, setGroupName] = useState("");
    const [description, setDescription] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchUsers();
            setGroupName("");
            setDescription("");
            setSelectedUsers([]);
            setSearchTerm("");
        }
    }, [isOpen]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const snapshot = await get(ref(db, 'users'));
            if (snapshot.exists()) {
                const data = snapshot.val();
                const userList = Object.keys(data)
                    .filter(key => key !== currentUser.uid)
                    .map(key => ({
                        id: key,
                        ...data[key]
                    }));
                setAllUsers(userList);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    };

    const toggleUser = (user) => {
        if (selectedUsers.find(u => u.id === user.id)) {
            setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
        } else {
            setSelectedUsers(prev => [...prev, user]);
        }
    };

    const handleCreateGroup = async () => {
        if (!groupName.trim() || selectedUsers.length === 0 || creating) return;

        setCreating(true);
        try {
            const result = await GroupService.createGroup({
                name: groupName,
                description: description,
                photo: '',
                memberIds: selectedUsers.map(u => u.id),
                creator: {
                    uid: currentUser.uid,
                    name: currentUser.name || currentUser.displayName || currentUser.email,
                    email: currentUser.email
                }
            });

            if (result.success) {
                showToast(`Group "${groupName.trim()}" created!`, 'success');
                onGroupCreated(result.chatId);
                onClose();
            } else {
                showToast(result.error || 'Failed to create group', 'error');
            }
        } catch (error) {
            console.error("Error creating group:", error);
            showToast('Failed to create group', 'error');
        } finally {
            setCreating(false);
        }
    };

    if (!isOpen) return null;

    const filteredUsers = allUsers.filter(u =>
        (u.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl scale-100 animate-in zoom-in-95 duration-200 mx-4 flex flex-col max-h-[85vh]">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <Users size={24} className="text-indigo-600" />
                        Create New Group
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 bg-slate-100 rounded-full">
                        <X size={20} />
                    </button>
                </div>

                <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                    {/* Group Name */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Group Name</label>
                        <input
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            className="w-full border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                            placeholder="e.g. Project Alpha Team"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none resize-none text-sm"
                            placeholder="What is this group about?"
                            rows={2}
                        />
                    </div>

                    {/* Member Selection */}
                    <div className="flex-1 flex flex-col min-h-0">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Add Members ({selectedUsers.length})</label>
                        <div className="relative mb-2">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
                                placeholder="Search people..."
                            />
                        </div>

                        <div className="flex-1 overflow-y-auto border border-slate-100 rounded-lg bg-slate-50 p-2 space-y-1 custom-scrollbar">
                            {loading ? (
                                <div className="flex justify-center p-4"><Loader2 className="animate-spin text-slate-400" /></div>
                            ) : filteredUsers.length === 0 ? (
                                <div className="text-center text-slate-400 text-sm p-4">No users found</div>
                            ) : (
                                filteredUsers.map(user => {
                                    const isSelected = !!selectedUsers.find(u => u.id === user.id);
                                    return (
                                        <div
                                            key={user.id}
                                            onClick={() => toggleUser(user)}
                                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-white border border-transparent'}`}
                                        >
                                            <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 bg-white'}`}>
                                                {isSelected && <Check size={12} className="text-white" />}
                                            </div>
                                            <img src={user.photo || `https://ui-avatars.com/api/?name=${user.name}`} className="w-8 h-8 rounded-full" alt="" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-800">{user.name}</p>
                                                <p className="text-xs text-slate-400 truncate">{user.email}</p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg font-medium">Cancel</button>
                    <button
                        onClick={handleCreateGroup}
                        disabled={creating || !groupName.trim() || selectedUsers.length === 0}
                        className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {creating && <Loader2 size={16} className="animate-spin" />}
                        Create Group
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateGroupModal;
